import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OffersSnackbar, ProductAisleV2, SnackBar } from '@desquared/wave-components-library';
import {
  addProduct,
  getBundleOfferDescription,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  getProductPropsWithCleanUrl,
  openListSideSheet,
  removeProduct,
  transformProduct,
} from '@/utils/products.js';
import { checkListStatus } from '@/utils/lists.js';
import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import Link from 'next/link';
import { getProductInfo, selectItem } from '@/utils/analytics.js';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar.js';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId.js';
import { BundleOfferSideSheetStore } from '@/store/sideSheet.js';
import { productsForOrder } from '@/store/cart.js';
import { getPersonalizedProducts, getV31AvailableProducts } from '@/api/Products.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import { CurrencyContext } from '@/contexts/CurrencyProvider.jsx';
import { useTranslations } from 'next-intl';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary.js';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useProductTags from '@/hooks/useProductTags.js';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { useConfiguration } from '@/contexts/ConfigContext.jsx';

export default function PersonalizedProducts({
  title,
  productsLink,
  linkText,
  isMobile,
  hasPadding,
  showMore,
  hideLastTile,
  defaultProductImage,
  list,
  analyticsFunction,
}) {
  const [personalizedProducts, setPersonalizedProducts] = useState([]);
  const router = useRouter();
  const [homePageProducts, setHomePageProducts] = useState([]);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [productsOrder, setProductsOrder] = useState();
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const t = useTranslations('common');
  const { formatter } = useContext(CurrencyContext);
  const { locale } = useRouter();
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const { config: configuration } = useConfiguration();
  const personalizedProductsEnabled = configuration?.catalog?.product?.recommendations?.user?.enabled ?? false;
  const a = useTranslations('common.asides');

  const { data: personalizedProductsData } = useQuery({
    queryKey: ['personalizedProducts', locale],
    queryFn: getPersonalizedProducts,
    staleTime: 1000 * 60 * 60 * 1, // Cache the data for 24 hours
    enabled: personalizedProductsEnabled,
  });

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);

    function getHomePageProducts(productsSkus) {
      const selectedCollectionTypes = getCollectionTypes();
      getV31AvailableProducts(productsSkus, selectedCollectionTypes, router.locale).then(result => {
        if (result) {
          const _newProducts =
            result.length > 0
              ? result
                  .map(product =>
                    transformProduct(
                      product,
                      setShowSnackbar,
                      undefined,
                      productTags,
                      bundleOffersSummary,
                      undefined,
                      undefined,
                      undefined,
                      personalizedProductsIds,
                      a
                    )
                  )
                  .map(p => ({ ...p, categoryIds: p.categories, _id: p.id }))
                  .map(enhanceProductWithSlug)
                  .map(p => {
                    let result = p.slug.slice(1);
                    return { ...p, slug: result };
                  })
              : [];

          let sortedArray = productsSkus
            .map(sku => _newProducts.find(product => product.sku === sku))
            .filter(product => product !== undefined);
          setPersonalizedProducts(sortedArray);
          setHomePageProducts(result);
        }
      });
    }

    if (personalizedProductsData) {
      const productsSkus = personalizedProductsData.map(product => product.sku);
      getHomePageProducts(productsSkus);
    }
    // eslint-disable-next-line
    return () => {
      productsForOrder$.unsubscribe();
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, [personalizedProductsData, productTags, bundleOffersSummary, personalizedProductsIds]);

  useEffect(() => {
    const _newProducts =
      homePageProducts.length > 0
        ? homePageProducts
            .map(product =>
              transformProduct(
                product,
                setShowSnackbar,
                undefined,
                productTags,
                bundleOffersSummary,
                undefined,
                undefined,
                undefined,
                personalizedProductsIds,
                a
              )
            )
            .map(p => ({ ...p, categoryIds: p.categories, _id: p.id }))
            .map(enhanceProductWithSlug)
            .map(p => {
              let result = p.slug.slice(1);
              return { ...p, slug: result };
            })
        : [];
    let sortedArray = personalizedProductsData
      ?.map(product => product.sku)
      ?.map(sku => _newProducts.find(product => product.sku === sku))
      .filter(product => product !== undefined);
    setPersonalizedProducts(sortedArray);
  }, [productTags, bundleOffersSummary, productsOrder, personalizedProductsIds]);

  if (!personalizedProducts || personalizedProducts.length === 0) return null;

  return (
    <>
      <ProductAisleV2
        title={title}
        products={personalizedProducts}
        productsLink={productsLink}
        linkText={linkText}
        tilesSize={'default'}
        isMobile={isMobile}
        hasPadding={hasPadding}
        onListClick={openListSideSheet}
        checkStatus={checkListStatus}
        analyticsFunction={analyticsFunction}
        list={list}
        addProduct={product =>
          addProduct(
            product,
            false,
            {
              item_list_id: list ?? 'personalizedProducts',
              item_list_name: list ?? 'personalizedProducts',
            },
            false,
            t('asides.side_cart.no_comment')
          )
        }
        removeProduct={product =>
          removeProduct(
            product,
            {
              item_list_id: list ?? 'personalizedProducts',
              item_list_name: list ?? 'personalizedProducts',
            },
            t('asides.side_cart.no_comment')
          )
        }
        showMore={showMore && showMore !== 'undefined' ? showMore : true}
        allText={t('product_aisles.show_more_default_text')}
        hideLastTile={hideLastTile}
        defaultQsText={t('qs.default')}
        defaultOutOfStockText={t('asides.out_of_stock')}
        defaultQsUnavailableText={t('qs.default_unavailable')}
        ImageComponent={CustomImage}
        offerLabelHasRibbon={process.env.offerLabelHasRibbon}
        isTileDetailsAnimated={process.env.isTileDetailsAnimated}
        ctaIcon={process.env.ctaIcon}
        listIcon={process.env.listIcon}
        LinkComponent={props => {
          const propsWithCleanUrl = getProductPropsWithCleanUrl(props);
          return (
            <Link
              prefetch={false}
              {...propsWithCleanUrl}
              onClick={() => {
                // we add slice because from ProductTile slug is / + slug. And personalizedProducts dont have leading slash
                const product = getProductBasedOnSlug(personalizedProducts, propsWithCleanUrl.href.slice(1));

                if (product) {
                  selectItem(getProductInfo(product?.originalValues), {
                    item_list_id: 'homepagePersonalizedAisle',
                    item_list_name: 'homepagePersonalizedAisle',
                    index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                  });
                }
              }}
            />
          );
        }}
        setMaxSnackBar={setShowSnackbar}
        currencyFormatter={formatter}
        defaultProductImage={defaultProductImage}
        forYouLabel={t('user.profile.menu.for_you')}
      />
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('snackbar')} position={'center'} />
      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        hasShadow={true}
      />
    </>
  );
}
